import React from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';

const ClientList = ({ clients, onClientClick, filter }) => {
    // Debug logging
    // console.log('ClientList received clients:', clients);
    // console.log('ClientList received filter:', filter);
    
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'connected':
                return 'text-green-500';
            case 'disconnected':
                return 'text-red-500';
            case 'not configured':
                return 'text-yellow-500';
            default:
                return 'text-gray-500';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'connected':
                return 'mdi:check-circle';
            case 'disconnected':
                return 'mdi:close-circle';
            case 'not configured':
                return 'mdi:alert-circle';
            default:
                return 'mdi:help-circle';
        }
    };

    const getStatusBadgeColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'connected':
                return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
            case 'disconnected':
                return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
            case 'not configured':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
        }
    };

    const filterClients = (clients, filter) => {
        // console.log('filterClients called with:', { clients, filter });
        if (!clients) {
            // console.log('No clients provided, returning empty array');
            return [];
        }
        
        let filtered = [];
        switch (filter) {
            case 'connected':
                filtered = clients.filter(client => client.status?.toLowerCase() === 'connected');
                break;
            case 'disconnected':
                filtered = clients.filter(client => client.status?.toLowerCase() === 'disconnected');
                break;
            case 'notConfigured':
                filtered = clients.filter(client => client.status?.toLowerCase() === 'not configured');
                break;
            case 'issues':
                filtered = clients.filter(client => 
                    client.status?.toLowerCase() === 'disconnected' || 
                    client.status?.toLowerCase() === 'not configured'
                );
                break;
            default:
                filtered = clients;
        }
        
        // console.log('Filtered clients result:', filtered);
        return filtered;
    };

    const filteredClients = filterClients(clients, filter);
    
    // Debug: Log the first client to see its structure
    if (clients && clients.length > 0) {
        // console.log('First client structure:', clients[0]);
        // console.log('All client statuses:', clients.map(c => ({ clientId: c.clientId, status: c.status })));
    }

    if (!clients || clients.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <Icon icon="mdi:database-off" className="text-4xl text-font-secondary mb-2" />
                <p className="text-font-secondary">No client accounts found</p>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col gap-3">
            {filteredClients.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                    <Icon icon="mdi:filter-off" className="text-4xl text-font-secondary mb-2" />
                    <p className="text-font-secondary">No clients match the current filter</p>
                </div>
            ) : (
                filteredClients.map((client) => (
                    <div
                        key={client.clientId}
                        onClick={() => onClientClick(client)}
                        className="p-3 border border-border interactive-card-container rounded-lg hover:bg-bgAccent/80 cursor-pointer transition-colors flex flex-col gap-2"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                                <Icon 
                                    icon={getStatusIcon(client.status)} 
                                    className={`text-3xl ${getStatusColor(client.status)}`} 
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-bold text-font-primary text-base capitalize">
                                            {client.clientId}
                                        </span>
                                        <span className={`p-1.5 rounded-md text-xs font-semibold ${getStatusBadgeColor(client.status)}`}>
                                            {client.status}
                                        </span>
                                    </div>
                                    
                                    <div className="text-sm text-font-secondary flex flex-row gap-3 items-center">
                                        {client.container && (
                                            <div className="flex items-center gap-1">
                                                <Icon icon="mdi:folder" className="text-sm" />
                                                <span className="font-mono text-xs font-semibold">{client.container}</span>
                                            </div>
                                        )}
                                        
                                        {client.responseTime && (
                                            <div className="flex items-center gap-1">
                                                <Icon icon="mdi:clock-outline" className="text-xs" />
                                                <span>{client.responseTime}</span>
                                            </div>
                                        )}
                                        
                                        {client.hasBlobs !== undefined && (
                                            <div className="flex items-center gap-1">
                                                <Icon 
                                                    icon={client.hasBlobs ? "mdi:file-multiple" : "mdi:file-multiple-outline"} 
                                                    className="text-xs" 
                                                />
                                                <span>Blobs: {client.hasBlobs ? 'Yes' : 'No'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                {/* Special indicators */}
                                {client.isKnownIssue && (
                                    <div className="flex items-center gap-1 px-2 py-1 bg-yellow-400 bg-opacity-20 rounded text-xs md:text-base">
                                        <Icon icon="mdi:lock" className="text-yellow-500" />
                                    </div>
                                )}
                                
                                {client.isNotConfigured && (
                                    <div className="flex items-center px-2 py-1 bg-blue-400 bg-opacity-20 rounded text-xs md:text-base">
                                        <Icon icon="mdi:information" className="text-blue-500" />
                                    </div>
                                )}
                                
                                {client.error && (
                                    <div className="flex items-center px-2 py-1 bg-red-400 bg-opacity-20 rounded text-xs md:text-base">
                                        <Icon icon="mdi:alert-circle" className="text-red-500" />
                                    </div>
                                )}
                                
                                <Icon icon="mdi:chevron-right" className="text-font-secondary" />
                            </div>
                        </div>
                        
                        {/* Error message preview - only show for actual errors, not "not configured" */}
                        {client.error && client.status?.toLowerCase() !== 'not configured' && (
                            <div className="p-2 bg-red-400 bg-opacity-10 rounded text-xs text-font-primary">
                                {client.error.length > 100 
                                    ? `${client.error.substring(0, 100)}...` 
                                    : client.error
                                }
                            </div>
                        )}
                    </div>
                ))
            )}
            
            {/* Filter summary */}
            {filter !== 'all' && (
                <div className="text-xs text-font-secondary text-center pt-2 border-t border-bgAccent">
                    Showing {filteredClients.length} of {clients.length} clients
                </div>
            )}
        </div>
    );
};

export default ClientList;
